import { Check, ArrowRight } from "lucide-react";
import { SectionHeading } from "@/components/shared/SectionHeading";
import { RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { openSignIn } from "@/lib/signInModal";

const TIERS = [
  {
    role: "driver",
    name: "Driver",
    features: ["Access to available loads & relief assignments", "Secured payment visibility", "Navigation handoff to Google Maps / Waze", "Standard driver verification"],
  },
  {
    role: "owner",
    name: "Driver / Owner Operator",
    features: ["Everything in Driver", "Request relief drivers for your own truck", "Priority matching on relay assignments", "Equipment profile & trailer preferences"],
    featured: true,
  },
  {
    role: "carrier",
    name: "Carrier / Fleet",
    features: ["Everything in Driver / Owner Operator", "Post driver requests across your fleet", "USDOT & fleet-level verification", "Multi-seat account management"],
  },
];

export const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-white">
    <div className="max-w-6xl mx-auto px-5 md:px-8">
      <SectionHeading eyebrow="RelayRigs Membership" title="Simple Pricing for Every Role" align="center" testId="pricing" />

      <RevealStagger className="mt-14 grid md:grid-cols-3 gap-6">
        {TIERS.map((t) => (
          <RevealItem key={t.role}>
            <div
              data-testid={`pricing-card-${t.role}`}
              className={`h-full rounded-[1.75rem] p-8 border flex flex-col ${
                t.featured ? "bg-rr-navy border-rr-navy text-white shadow-2xl shadow-rr-navy/20 lg:-translate-y-3" : "bg-white border-rr-line text-rr-navy"
              }`}
            >
              {t.featured && <div className="text-xs font-bold uppercase tracking-widest text-rr-orangeLight mb-3">Most Popular</div>}
              <h3 className="font-heading text-xl font-bold">{t.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span data-testid={`pricing-fee-${t.role}`} className="font-heading text-4xl font-extrabold">5%</span>
                <span className={`text-sm font-semibold mb-1 ${t.featured ? "text-white/60" : "text-rr-muted"}`}>platform fee</span>
              </div>
              <p className={`text-xs mt-1 ${t.featured ? "text-white/50" : "text-rr-muted"}`}>Taken from each completed load or assignment payout</p>
              <ul className="mt-6 space-y-3 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <Check size={16} className={`shrink-0 mt-0.5 ${t.featured ? "text-rr-orangeLight" : "text-rr-orange"}`} />
                    <span className={t.featured ? "text-white/80" : "text-rr-muted"}>{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => openSignIn("register", t.role)}
                data-testid={`pricing-subscribe-${t.role}`}
                className={`mt-8 inline-flex items-center justify-center gap-2 font-bold py-3.5 rounded-full transition-all hover:-translate-y-1 ${
                  t.featured ? "bg-rr-orange text-white hover:bg-white hover:text-rr-navy" : "bg-rr-navy text-white hover:bg-rr-orange"
                }`}
              >
                Get Started <ArrowRight size={16} />
              </button>
            </div>
          </RevealItem>
        ))}
      </RevealStagger>

      <p data-testid="pricing-disclaimer" className="text-xs text-rr-muted text-center max-w-2xl mx-auto mt-10 leading-relaxed">
        Pricing and included features are subject to final launch terms. Pre-registration does not automatically begin a paid subscription.
      </p>
    </div>
  </section>
);
